import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { motion, AnimatePresence } from "framer-motion";
import {
  TrendingUp,
  ArrowRight,
  CheckCircle,
  LogOut,
  Mail,
  Loader2,
  User,
  Settings,
  ChevronDown,
  CreditCard,
  Coins,
  GraduationCap,
  Target,
  ChevronRight,
  Sparkles,
  Lock,
  LayoutDashboard,
} from "lucide-react";
import { toast } from "sonner";
import AuthModal from "@/components/AuthModal";
import Footer from "@/components/Footer";

import HomepageQuizFlow from "@/components/HomepageQuizFlow";
import WealthsimpleStepsModal from "@/components/WealthsimpleStepsModal";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Facts carousel data - 20 rotating statistics every 2 seconds
const INVESTING_FACTS = [
  { stat: "7,000+", text: "International students arrive in Canada each year with no investing knowledge" },
  { stat: "73%", text: "of Canadians aged 18-34 say they wish they started investing earlier" },
  { stat: "$7,000", text: "Annual TFSA contribution room - tax-free growth for life" },
  { stat: "10.5%", text: "Average annual return of the S&P 500 over the past 30 years" },
  { stat: "$1M+", text: "What $200/month could become in 40 years at 8% returns" },
  { stat: "68%", text: "of young Canadians don't know the difference between TFSA and RRSP" },
  { stat: "5 years", text: "Average time international students wait before opening an investment account" },
  { stat: "$47,000", text: "Average student loan debt in Canada - proper budgeting is essential" },
  { stat: "2x", text: "Your money can double every 9 years with 8% returns (Rule of 72)" },
  { stat: "30%", text: "of Canadians have zero retirement savings - don't be one of them" },
  { stat: "18-25", text: "The best age range to start investing - time is your greatest asset" },
  { stat: "$6,500", text: "Annual RRSP contribution limit for students - reduce taxes now" },
  { stat: "40%", text: "of millennials have never invested in the stock market" },
  { stat: "7-10%", text: "Average annual return of Canadian index funds like XEQT" },
  { stat: "$500", text: "Minimum to open most investment accounts in Canada" },
  { stat: "3-6 mo", text: "Recommended emergency fund before heavy investing" },
  { stat: "15 min", text: "Time to open a self-directed investment account online" },
  { stat: "0.2%", text: "Average MER for low-cost index ETFs vs 2%+ for mutual funds" },
  { stat: "$95K", text: "Average Canadian household net worth under 35 years old" },
  { stat: "52%", text: "of Gen Z say financial literacy should be taught in schools" },
];

const CategoryHome = () => {
  const [authOpen, setAuthOpen] = useState(false);
  const [wealthsimpleModalOpen, setWealthsimpleModalOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);

  // Rotating facts carousel - every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFactIndex((prev) => (prev + 1) % INVESTING_FACTS.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Check if user is logged in
  useEffect(() => {
    const checkUserAndOnboarding = async () => {
      let token = localStorage.getItem("user_token") || localStorage.getItem("token");
      let userData = localStorage.getItem("user_data") || localStorage.getItem("user");
      
      if (!token || !userData) {
        token = sessionStorage.getItem("user_token");
        userData = sessionStorage.getItem("user_data");
      }
      
      let currentUser = null;
      if (token && userData) {
        try {
          const parsed = JSON.parse(userData);
          if (parsed) {
            currentUser = parsed;
            setUser(parsed);
            setIsLoggedIn(true);
          }
        } catch (e) {
          console.error("Failed to parse user data");
        }
      }
      
      // Check if user has completed onboarding (both quizzes)
      const onboardingComplete = localStorage.getItem("onboardingComplete");
      const recommendedCategory = localStorage.getItem("recommendedCategory");
      const investorType = localStorage.getItem("investorQuizCompleted");
      
      // Check localStorage first - if ALL required data exists, set state immediately
      if (onboardingComplete === "true" && recommendedCategory && investorType) {
        setHasCompletedOnboarding(true);
        return; // No need to fetch from database
      }
      
      // If user is logged in, ALWAYS check database to ensure sync
      if (currentUser) {
        try {
          const userId = currentUser.email || currentUser.id;
          const response = await fetch(`${API_URL}/api/progress/${userId}`);
          if (response.ok) {
            const progress = await response.json();
            
            // If onboarding data exists in database, restore to localStorage and update state
            if (progress.onboarding && progress.onboarding.investor_type && progress.onboarding.recommended_category) {
              localStorage.setItem("investorQuizCompleted", progress.onboarding.investor_type);
              localStorage.setItem("recommendedCategory", progress.onboarding.recommended_category);
              localStorage.setItem("onboardingComplete", "true");
              
              // Update state to trigger re-render
              setHasCompletedOnboarding(true);
            }
          }
        } catch (error) {
          console.error("Error fetching user progress:", error);
        }
      }
    };
    
    checkUserAndOnboarding();
  }, []);

  const handleAuthSuccess = async (userData) => {
    // Update user state immediately
    setUser(userData);
    setIsLoggedIn(true);
    
    // Check localStorage synchronously (it was just updated during login)
    const onboardingComplete = localStorage.getItem("onboardingComplete");
    const recommendedCategory = localStorage.getItem("recommendedCategory");
    const investorType = localStorage.getItem("investorQuizCompleted");
    
    // If localStorage has the data, update state immediately
    if ((onboardingComplete === "true" || investorType) && recommendedCategory) {
      setHasCompletedOnboarding(true);
    } else {
      // If no localStorage data, fetch from database
      try {
        const userId = userData.email || userData.id;
        const response = await fetch(`${API_URL}/api/progress/${userId}`);
        if (response.ok) {
          const progress = await response.json();
          if (progress.onboarding && progress.onboarding.investor_type) {
            // Update localStorage
            localStorage.setItem("investorQuizCompleted", progress.onboarding.investor_type);
            localStorage.setItem("recommendedCategory", progress.onboarding.recommended_category || "startInvesting");
            localStorage.setItem("onboardingComplete", "true");
            // Update state
            setHasCompletedOnboarding(true);
          }
        }
      } catch (error) {
        console.error("Error checking onboarding status:", error);
      }
    }
    
    // Force a small delay then reload to ensure all state is consistent
    // This is needed because React state batching can cause visual inconsistencies
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const handleLogout = () => {
    localStorage.removeItem("user_token");
    localStorage.removeItem("user_data");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("remember_me");
    sessionStorage.removeItem("user_token");
    sessionStorage.removeItem("user_data");
    setUser(null);
    setIsLoggedIn(false);
    toast.success("Logged out successfully");
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;

    setNewsletterLoading(true);
    try {
      await axios.post(`${API_URL}/api/subscribers`, {
        email: newsletterEmail,
        phone: "",
        source: "newsletter_home",
      });
      setNewsletterSuccess(true);
      localStorage.setItem("compoundr_subscribed", "true");
      toast.success("Welcome to Compoundr!");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("You're already subscribed!");
        setNewsletterSuccess(true);
        localStorage.setItem("compoundr_subscribed", "true");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setNewsletterLoading(false);
    }
  };

  const categories = [
    {
      id: "debt-free",
      icon: CreditCard,
      title: "Get Debt-Free",
      emoji: "💳",
      description: "Crush your debt and reclaim your financial freedom",
      benefits: [
        "Calculate your debt-free date",
        "Track hidden subscriptions",
        "Find money you're wasting",
        "Learn proven payoff strategies"
      ],
      perfectFor: "People with credit card debt, student loans, or any high-interest debt",
      buttonText: "Start Debt Elimination",
      colorClass: "text-red-500",
      gradientClass: "from-red-500 to-red-700",
      hoverClass: "hover:border-red-500/50 hover:shadow-red-500/20",
      iconBg: "bg-gradient-to-br from-red-500 to-red-700",
      link: "/debt-free"
    },
    {
      id: "start-investing",
      icon: TrendingUp,
      title: "Start Investing",
      emoji: "📈",
      description: "Begin your wealth-building journey with confidence",
      benefits: [
        "Practice with $10K virtual cash",
        "Take investor quiz & get your plan",
        "Learn to analyze stocks with AI",
        "Build your first portfolio"
      ],
      perfectFor: "Beginners ready to invest, people debt-free and looking to grow wealth",
      buttonText: "Start Investing",
      colorClass: "text-emerald-500",
      gradientClass: "from-emerald-500 to-emerald-700",
      hoverClass: "hover:border-emerald-500/50 hover:shadow-emerald-500/20",
      iconBg: "bg-gradient-to-br from-emerald-500 to-emerald-700",
      highlight: true,
      badge: "Most Popular",
      link: "/start-investing"
    },
    {
      id: "build-wealth",
      icon: Coins,
      title: "Build Wealth",
      emoji: "💰",
      description: "Grow your money and plan for your financial future",
      benefits: [
        "Calculate compound growth",
        "Track your net worth",
        "Project retirement savings",
        "Explore salary potential"
      ],
      perfectFor: "People already investing who want to accelerate growth and plan long-term",
      buttonText: "Start Wealth Building",
      colorClass: "text-amber-500",
      gradientClass: "from-amber-500 to-amber-600",
      hoverClass: "hover:border-amber-500/50 hover:shadow-amber-500/20",
      iconBg: "bg-gradient-to-br from-amber-500 to-amber-600",
      link: "/build-wealth"
    },
    {
      id: "learn-finance",
      icon: GraduationCap,
      title: "Learn Finance",
      emoji: "📚",
      description: "Master money management and investing from scratch",
      benefits: [
        "105+ financial terms explained",
        "Interactive quizzes & AI chat",
        "Daily market updates with insights",
        "Real-world examples & case studies"
      ],
      perfectFor: "Complete beginners, students, anyone wanting to become financially literate",
      buttonText: "Start Learning",
      colorClass: "text-blue-500",
      gradientClass: "from-blue-500 to-blue-700",
      hoverClass: "hover:border-blue-500/50 hover:shadow-blue-500/20",
      iconBg: "bg-gradient-to-br from-blue-500 to-blue-700",
      link: "/learn-finance"
    },
    {
      id: "plan-save",
      icon: Target,
      title: "Plan & Save",
      emoji: "🎯",
      description: "Set goals and build smart saving strategies",
      benefits: [
        "Budget for trips and big purchases",
        "Split expenses with friends",
        "Track subscriptions & spending",
        "Calculate savings goals"
      ],
      perfectFor: "People saving for specific goals (vacation, house, wedding, emergency fund)",
      buttonText: "Create Your Plan",
      colorClass: "text-purple-500",
      gradientClass: "from-purple-500 to-purple-700",
      hoverClass: "hover:border-purple-500/50 hover:shadow-purple-500/20",
      iconBg: "bg-gradient-to-br from-purple-500 to-purple-700",
      link: "/plan-save"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50">
      {/* ========== MOBILE VERSION ========== */}
      <div className="md:hidden">
        {/* Mobile Hero - Clean and Simple */}
        <section className="gradient-mesh py-6 px-4">
          <div className="text-center">
            <Badge className="bg-emerald-100 text-emerald-700 mb-2 hover:bg-emerald-100 text-xs">
              For Canadian Students
            </Badge>
            <h1 className="text-xl font-heading font-bold text-navy-700 mb-2 leading-tight">
              Start Investing in Canada
              <span className="text-emerald-500"> Without the Hype</span>
            </h1>
            <p className="text-xs text-slate-600 mb-4 leading-relaxed">
              Clear, beginner-friendly education for Canadian young adults. Build confidence and grow your wealth.
            </p>

            {/* Sign In / Welcome - Mobile (Compact) */}
            {!isLoggedIn ? (
              <div className="bg-navy-700 rounded-xl p-4 mb-4">
                <p className="text-emerald-400 font-semibold text-sm mb-1">Get My Money Right</p>
                <p className="text-slate-300 text-xs mb-3">Your journey to financial freedom</p>
                <Button
                  onClick={() => setAuthOpen(true)}
                  className="w-full h-10 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg"
                  data-testid="mobile-signup-btn"
                >
                  Get Started Free
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span className="text-emerald-700 font-medium text-sm">
                    Welcome, {user?.email?.split('@')[0]}! All tools unlocked.
                  </span>
                </div>
              </div>
            )}

            {/* Quiz Cards - Mobile - Only show if NOT completed onboarding */}
            {!hasCompletedOnboarding && (
              <div className="space-y-3 mb-4">
                {/* Step 1: Investor Type Quiz - FREE FOR EVERYONE */}
                <Link to="/tools?tool=investor-quiz&mode=mobile-free" className="block">
                  <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-4 text-white text-left shadow-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                        <span className="text-lg font-bold">1</span>
                      </div>
                      <div className="flex-1">
                        <p className="text-white text-xs uppercase tracking-wide font-black flex items-center gap-1">
                          Get Started Here <ChevronDown className="w-3 h-3" />
                        </p>
                        <h3 className="font-bold text-base">Investor Type Quiz</h3>
                        <p className="text-blue-100 text-xs font-semibold">Free • No sign up required</p>
                      </div>
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </Link>

                {/* Step 2: Category Recommendation Quiz */}
                {isLoggedIn ? (
                  <Link to="/category-quiz" className="block">
                    <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl p-4 text-white text-left">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                          <span className="text-lg font-bold">2</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-base">Category Recommendation</h3>
                          <p className="text-emerald-100 text-xs">Find your starting point</p>
                        </div>
                        <ArrowRight className="w-5 h-5" />
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div onClick={() => setAuthOpen(true)} className="bg-gradient-to-r from-slate-400 to-slate-500 rounded-xl p-4 text-white text-left cursor-pointer">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                        <Lock className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-base">Category Recommendation</h3>
                        <p className="text-slate-200 text-xs">Sign in to unlock Step 2</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Dashboard Link - Only for users who completed onboarding */}
            {isLoggedIn && hasCompletedOnboarding && (
              <div className="mb-4">
                <Link to="/dashboard" className="block">
                  <div className="bg-gradient-to-r from-navy-700 to-navy-800 rounded-xl p-4 text-white text-left shadow-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                        <LayoutDashboard className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-base">Go to Your Dashboard</h3>
                        <p className="text-slate-300 text-xs">View personalized recommendations</p>
                      </div>
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* Mobile Primary Goal Categories - LOCKED unless signed in */}
        <section className="py-6 px-4 bg-white">
          <h2 className="text-lg font-heading font-bold text-navy-700 mb-4 text-center">
            Choose Your Goal
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {categories.map((cat) => (
              isLoggedIn ? (
                <Link key={cat.id} to={cat.link} className="block">
                  <div className={`bg-white border-2 border-slate-200 rounded-xl p-4 text-center hover:border-slate-300 transition-colors ${cat.highlight ? 'border-emerald-300 bg-emerald-50' : ''}`}>
                    <span className="text-2xl mb-2 block">{cat.emoji}</span>
                    <h3 className="font-semibold text-sm text-navy-700">{cat.title}</h3>
                    {cat.badge && (
                      <span className="text-[10px] text-emerald-600 font-medium">★ {cat.badge}</span>
                    )}
                  </div>
                </Link>
              ) : (
                <div key={cat.id} onClick={() => setAuthOpen(true)} className="cursor-pointer">
                  <div className={`bg-slate-100 border-2 border-slate-200 rounded-xl p-4 text-center relative ${cat.highlight ? 'border-slate-300' : ''}`}>
                    <div className="absolute top-2 right-2">
                      <Lock className="w-3 h-3 text-slate-400" />
                    </div>
                    <span className="text-2xl mb-2 block opacity-60">{cat.emoji}</span>
                    <h3 className="font-semibold text-sm text-slate-500">{cat.title}</h3>
                    {cat.badge && (
                      <span className="text-[10px] text-slate-400 font-medium">★ {cat.badge}</span>
                    )}
                  </div>
                </div>
              )
            ))}
          </div>
          {!isLoggedIn && (
            <p className="text-center text-xs text-slate-500 mt-4">
              🔒 Sign in to access all categories
            </p>
          )}
        </section>

        {/* Mobile Wealthsimple Banner */}
        {isLoggedIn && (
          <section className="py-4 px-4 bg-[#00C853]">
            <div 
              onClick={() => setWealthsimpleModalOpen(true)}
              className="flex items-center justify-between text-white cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                  <span className="text-[#00C853] text-sm font-bold">W</span>
                </div>
                <span className="font-bold text-sm">Get free $25 towards your Wealthsimple TFSA</span>
              </div>
              <Button size="sm" className="bg-white text-[#00C853] hover:bg-white/90 text-sm px-4 rounded-full font-bold">
                Claim →
              </Button>
            </div>
          </section>
        )}

        {/* Mobile Quick Links */}
        {isLoggedIn && (
          <section className="py-6 px-4 bg-slate-50">
            <h2 className="text-lg font-heading font-bold text-navy-700 mb-4">Quick Access</h2>
            <div className="space-y-2">
              <Link to="/tools?tool=simulator" className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-sm">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm text-navy-700">Stock Simulator</h3>
                  <p className="text-xs text-slate-500">Practice trading</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </Link>
              <Link to="/tools" className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-sm">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm text-navy-700">All Tools</h3>
                  <p className="text-xs text-slate-500">Calculators & quizzes</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </Link>
              <Link to="/blog" className="flex items-center gap-3 bg-white rounded-xl p-3 shadow-sm">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm text-navy-700">Learn</h3>
                  <p className="text-xs text-slate-500">Videos & articles</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </Link>
            </div>
          </section>
        )}

        {/* Mobile Newsletter Section */}
        <section className="py-8 px-4 bg-gradient-to-br from-emerald-600 to-emerald-700">
          <div className="text-center">
            <h2 className="text-xl font-heading font-bold text-white mb-2">Stay in the Loop</h2>
            <p className="text-emerald-100 text-sm mb-4">
              Get weekly investing tips and updates
            </p>
            {newsletterSuccess ? (
              <div className="flex items-center justify-center gap-2 text-white">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">You&apos;re subscribed!</span>
              </div>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); handleNewsletterSubmit(); }} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="flex-1 bg-white/20 border-white/30 text-white placeholder:text-emerald-100"
                  required
                />
                <Button 
                  type="submit"
                  disabled={newsletterLoading}
                  className="bg-white hover:bg-emerald-50 text-emerald-700 px-4"
                >
                  {newsletterLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                </Button>
              </form>
            )}
          </div>
        </section>
      </div>

      {/* ========== DESKTOP VERSION ========== */}
      <div className="hidden md:block">
      {/* Hero Section with Account CTA */}
      <section className="relative gradient-mesh pt-24 pb-12 lg:pt-32 lg:pb-16 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge className="bg-emerald-100 text-emerald-700 mb-4 hover:bg-emerald-100">
              🇨🇦 For Canadian Students
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-navy-700 mb-6 leading-tight">
              Start Investing in Canada
              <span className="text-emerald-500"> Without the Hype</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-2xl mx-auto">
              Clear, beginner-friendly education for Canadian young adults and international
              students. Build confidence, start small, and grow your wealth
              over time. No get-rich-quick schemes, just real knowledge.
            </p>

            {/* Create Your Free Account Section - Only show when NOT logged in */}
            {!isLoggedIn && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mb-6 p-8 bg-gradient-to-br from-navy-700 to-navy-800 rounded-3xl shadow-floating max-w-2xl mx-auto"
              >
                <div className="text-center">
                  <h3 className="text-2xl font-heading font-bold text-white mb-1">
                    Get My Money Right
                  </h3>
                  <p className="text-emerald-400 font-medium text-sm mb-4">
                    Your journey to financial freedom starts here
                  </p>
                  <p className="text-slate-300 text-sm mb-6">
                    Sign up to unlock all quizzes, calculators, and save your personalized results!
                  </p>
                  <Button
                    onClick={() => setAuthOpen(true)}
                    className="w-full sm:w-auto h-12 bg-emerald-500 hover:bg-emerald-600 text-white text-lg font-semibold rounded-xl px-8"
                    data-testid="hero-signup-btn"
                  >
                    Get Started for Free
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Welcome Message for Logged In Users - Compact */}
            {isLoggedIn && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mb-6"
              >
                <div className="flex items-center justify-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-6 py-3 mx-auto w-fit">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <span className="text-emerald-700 font-medium">
                    Welcome back, {user?.email?.split("@")[0]}! All tools unlocked.
                  </span>
                </div>
              </motion.div>
            )}

            {/* Rotating Facts - 20 stats, every 2 seconds */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentFactIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="flex items-center justify-center gap-3 text-slate-600 bg-white/60 backdrop-blur-sm rounded-full px-6 py-3 mx-auto w-fit"
              >
                <span className="text-2xl font-bold text-emerald-600">{INVESTING_FACTS[currentFactIndex].stat}</span>
                <span className="text-sm">{INVESTING_FACTS[currentFactIndex].text}</span>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Integrated Quiz Flow Section - Only show if user hasn't completed onboarding */}
      {!hasCompletedOnboarding && (
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-white" data-testid="homepage-quiz-section">
          <HomepageQuizFlow 
            mobileFreeMode={!isLoggedIn}
            onRequireLogin={() => setAuthOpen(true)}
          />
        </section>
      )}

      {/* Dashboard Link Section - Only show for users who completed onboarding */}
      {isLoggedIn && hasCompletedOnboarding && (
        <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-gradient-to-br from-navy-700 to-navy-800 rounded-2xl p-8 shadow-lg">
              <LayoutDashboard className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Your Personalized Dashboard</h2>
              <p className="text-slate-300 mb-6">
                View your investor profile, recommended resources, and AI-powered financial tools.
              </p>
              <Link to="/dashboard">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white text-lg font-semibold px-8 h-12 rounded-xl">
                  Go to Dashboard
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Wealthsimple $25 Free Banner */}
      <section className="py-6 bg-[#00C853]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            onClick={() => setWealthsimpleModalOpen(true)}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 text-white hover:opacity-90 transition-opacity cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <span className="text-[#00C853] text-xl font-bold">W</span>
              </div>
              <span className="font-heading font-bold text-lg">
                Get free $25 towards your Wealthsimple TFSA account
              </span>
            </div>
            <Button className="bg-white text-[#00C853] hover:bg-white/90 text-lg px-8 py-6 rounded-full font-bold shadow-lg">
              Claim Free $25 →
            </Button>
          </div>
        </div>
      </section>

      {/* Category Selection Section */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-navy-700 mb-4">
              What&apos;s Your Primary Goal?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose your path and get personalized tools & guidance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.1 }}
                onMouseEnter={() => setHoveredCard(category.id)}
                onMouseLeave={() => setHoveredCard(null)}
                className={`relative bg-white rounded-3xl p-6 border border-slate-200 shadow-card
                  transition-all duration-300 cursor-pointer group
                  ${hoveredCard === category.id ? `${category.hoverClass} shadow-xl -translate-y-2` : ""}
                  ${category.highlight ? "ring-2 ring-emerald-500/50" : ""}`}
              >
                {category.badge && (
                  <div className="absolute -top-3 -right-3 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    {category.badge}
                  </div>
                )}

                {/* Lock icon for non-logged-in users */}
                {!isLoggedIn && (
                  <div className="absolute top-4 right-4">
                    <Lock className="w-4 h-4 text-slate-400" />
                  </div>
                )}

                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-12 h-12 ${category.iconBg} rounded-2xl flex items-center justify-center transform transition-transform group-hover:scale-110 group-hover:rotate-3`}>
                    <category.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-navy-700">{category.emoji} {category.title}</h3>
                  </div>
                </div>

                <p className="text-slate-600 text-sm mb-4">{category.description}</p>

                <ul className="space-y-2 mb-4">
                  {category.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-slate-500">
                      <Sparkles className={`w-3 h-3 ${category.colorClass} flex-shrink-0`} />
                      {benefit}
                    </li>
                  ))}
                </ul>

                <p className="text-xs text-slate-400 mb-4">
                  <strong>Perfect for:</strong> {category.perfectFor}
                </p>

                {isLoggedIn ? (
                  <Link to={category.link}>
                    <Button className={`w-full ${category.iconBg} text-white border-0 group-hover:shadow-lg`}>
                      {category.buttonText}
                      <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                ) : (
                  <Button 
                    onClick={() => setAuthOpen(true)}
                    className={`w-full ${category.iconBg} text-white border-0 group-hover:shadow-lg opacity-90`}
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Sign in to Access
                  </Button>
                )}
              </motion.div>
            ))}
          </div>

          {/* Alternative Options */}
          <div className="text-center space-y-6 mt-8">
            <p className="text-lg font-medium text-slate-600">Not sure where to start?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {isLoggedIn ? (
                <>
                  <Link to="/category-quiz">
                    <Button variant="outline" className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 px-8 py-6 text-base">
                      <Target className="w-5 h-5 mr-2" />
                      Take Our Quick Quiz
                    </Button>
                  </Link>
                  <Link to="/tools">
                    <Button variant="ghost" className="text-slate-600 hover:text-emerald-600 py-6 text-base">
                      Browse All Tools →
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 px-8 py-6 text-base"
                    onClick={() => setAuthOpen(true)}
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Sign in to Take Quiz
                  </Button>
                  <Button 
                    variant="ghost" 
                    className="text-slate-600 hover:text-emerald-600 py-6 text-base"
                    onClick={() => setAuthOpen(true)}
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Sign in to Browse Tools
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-heading font-bold text-white mb-3">
            Stay in the Loop
          </h2>
          <p className="text-emerald-100 mb-6">
            Get weekly investing tips, market updates, and new tool announcements
          </p>
          
          {newsletterSuccess ? (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white/20 rounded-2xl p-5 max-w-sm mx-auto"
            >
              <CheckCircle className="w-10 h-10 text-white mx-auto mb-2" />
              <p className="text-white font-medium">You&apos;re on the list!</p>
              <p className="text-emerald-100 text-sm">We&apos;ll notify you about new blogs, market updates, content, and AI tools!</p>
            </motion.div>
          ) : (
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <div className="flex-1 relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="pl-12 h-12 bg-white/90 border-0 rounded-xl text-slate-700 placeholder:text-slate-400"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={newsletterLoading}
                className="h-12 bg-navy-700 hover:bg-navy-800 text-white font-semibold rounded-xl px-6"
              >
                {newsletterLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Subscribe"
                )}
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      </div>
      {/* End Desktop Version */}

      <Footer />

      {/* Auth Modal */}
      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        onSuccess={handleAuthSuccess}
        required={true}
      />

      {/* Wealthsimple Steps Modal */}
      <WealthsimpleStepsModal 
        open={wealthsimpleModalOpen} 
        onOpenChange={setWealthsimpleModalOpen} 
      />

      {/* Chatbot */}
      
    </div>
  );
};

export default CategoryHome;
